import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { requisitionApi, procPdfApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, useMutation } from "../../hooks";
import type { RequisitionApprovalStage, RequisitionStatus } from "../../types";
import { ReasonModal } from "#/components/common/ReasonModal";

const STEPS: RequisitionStatus[] = [
  "DRAFT", "SUBMITTED", "DEPT_HEAD_APPROVED", "FINANCE_APPROVED", "PROCUREMENT_REVIEWED", "MD_APPROVED",
];

const STEP_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  DEPT_HEAD_APPROVED: "Dept Head",
  FINANCE_APPROVED: "Finance",
  PROCUREMENT_REVIEWED: "Procurement",
  MD_APPROVED: "MD Approved",
};

const TERMINAL_STATUSES: RequisitionStatus[] = ["MD_APPROVED", "REJECTED", "CANCELLED"];

export default function RequisitionViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [comments, setComments] = useState("");
  const [downloading, setDownloading] = useState(false);

  const { data: req, loading, error, reload } = useAsyncData(
    () => requisitionApi.get(Number(id)),
    [id],
  );

  // Finance must be able to correct/confirm the budget line at their approval stage.
  const [budgetLineDraft, setBudgetLineDraft] = useState("");
  useEffect(() => {
    setBudgetLineDraft(req?.budgetLine ?? "");
  }, [req?.id, req?.budgetLine]);

  const { mutate: doSubmit, loading: submitting } = useMutation(() =>
    requisitionApi.submit(Number(id)).then(() => reload()),
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { mutate: doCancel, loading: cancelling, error: cancelErr } = useMutation((reason: string) =>
    requisitionApi.cancel(Number(id), reason || undefined).then(() => { setShowCancelModal(false); reload(); }),
  );
  // budgetLine is only sent (and only accepted server-side) for the FINANCE stage — one
  // atomic call updates the budget line and advances the status together.
  const { mutate: doApprove, loading: approving, error: approveErr } = useMutation(
    (stage: RequisitionApprovalStage) =>
      requisitionApi
        .approve(Number(id), stage, comments, stage === "FINANCE" ? budgetLineDraft.trim() : undefined)
        .then(() => reload()),
  );
  const { mutate: doReject, loading: rejecting, error: rejectErr } = useMutation(() =>
    requisitionApi.reject(Number(id), comments).then(() => reload()),
  );

  async function downloadPdf() {
    if (!req) return;
    setDownloading(true);
    try {
      const blob = await procPdfApi.requisition(Number(id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pr-${req.serialNo ?? id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  }

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!req) return null;

  const currentStep = STEPS.indexOf(req.status as RequisitionStatus);

  const actionErr = approveErr || rejectErr;
  const isFinanceStage = req.nextApprovalStage === "FINANCE";

  const needsComments = !!req.nextApprovalStage;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/procurement/requisitions" className="text-sm text-emerald-700 hover:underline">&larr; Back to Requisitions</Link>
        <button onClick={downloadPdf} disabled={downloading}
          className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <Download className="h-4 w-4" />
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>

      <ProcForm formNo="PROC-FRM-01" title="Purchase Requisition" serialNo={req.serialNo} date={req.date}>

        {/* Approval progress */}
        <ProcSection title="Approval Progress">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {STEPS.map((step, idx) => {
              const done = currentStep > idx;
              const active = currentStep === idx;
              return (
                <div key={step} className="flex items-center gap-1 flex-shrink-0">
                  <div className={[
                    "flex items-center justify-center rounded-full h-8 px-3 text-xs font-bold whitespace-nowrap",
                    done ? "bg-emerald-600 text-white" : active ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-400",
                  ].join(" ")}>
                    {STEP_LABELS[step]}
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`h-0.5 w-6 flex-shrink-0 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ProcStatusBadge value={req.status} />
            {req.rejectionReason && (
              <span className="text-sm text-red-600">Reason: {req.rejectionReason}</span>
            )}
            {req.nextApproverRole && (
              <span className="text-sm text-slate-600">
                Awaiting: <strong>{req.currentStepName ?? req.nextApproverRole}</strong>
                {req.currentApproverUsername && <> — {req.currentApproverUsername}</>}
              </span>
            )}
          </div>
        </ProcSection>

        <ProcSection title="Requisition Details">
          <ProcGrid>
            <ProcField label="Department" value={req.departmentName} />
            {isFinanceStage ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Budget Line <span className="text-emerald-600 normal-case">(edit and approve to confirm)</span>
                </p>
                <input
                  className="mt-1 w-full rounded-lg border px-2.5 py-1.5 text-sm font-medium text-slate-800"
                  value={budgetLineDraft}
                  onChange={(e) => setBudgetLineDraft(e.target.value)}
                  placeholder="e.g. Capital Expenditure 2026"
                />
              </div>
            ) : (
              <ProcField label="Budget Line" value={req.budgetLine} />
            )}
            <ProcField label="Date" value={req.date ? new Date(req.date).toLocaleDateString("en-KE") : "—"} />
            <ProcField label="Purpose" value={req.purpose} span2 />
          </ProcGrid>
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {req.lines.map((l) => (
                  <tr key={l.id ?? l.lineOrder}>
                    <td className="px-3 py-2 text-slate-400">{l.lineOrder}</td>
                    <td className="px-3 py-2">{l.description}</td>
                    <td className="px-3 py-2 text-right">{l.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ProcSection>

        {/* Actions */}
        {!TERMINAL_STATUSES.includes(req.status) && (
          <ProcSection title="Actions">
            {actionErr && <ProcError message={actionErr} />}
            {needsComments && (
              <div className="mb-4">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Comments / Reason</label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={2}
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                  placeholder="Optional comments..."
                />
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              {req.status === "DRAFT" && (
                <button
                  type="button"
                  onClick={() => doSubmit()}
                  disabled={submitting}
                  className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Submit for Approval"}
                </button>
              )}
              {req.nextApprovalStage && (
                <>
                  <button
                    type="button"
                    onClick={() => doApprove(req.nextApprovalStage!)}
                    disabled={approving}
                    className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
                  >
                    {approving ? "Approving…" : `Approve${req.nextApproverRole ? ` as ${req.nextApproverRole}` : ""}`}
                  </button>
                  <button type="button" onClick={() => doReject()} disabled={rejecting} className="rounded-xl border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
                    {rejecting ? "Rejecting…" : "Reject"}
                  </button>
                </>
              )}
              {/* Cancel — an administrative override, distinct from Reject: it works regardless
                  of whose turn it is in the approval chain (no current-approver check), whereas
                  Reject only works for whoever's currently assigned at this step. Available at
                  any non-terminal status, not just DRAFT. Confirmed 2026-07-10: POST /{id}/cancel
                  already exists, same PROCUREMENT_APPROVE authority as reject. */}
              <button
                type="button"
                onClick={() => setShowCancelModal(true)}
                disabled={cancelling}
                className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                {cancelling ? "Cancelling…" : "Cancel Requisition"}
              </button>
            </div>
          </ProcSection>
        )}
      </ProcForm>

      <ReasonModal
        open={showCancelModal}
        title="Cancel Requisition"
        label="Reason for cancellation"
        placeholder="Optional — explain why this requisition is being cancelled"
        confirmLabel="Cancel Requisition"
        confirmTone="danger"
        submitting={cancelling}
        error={cancelErr}
        onCancel={() => setShowCancelModal(false)}
        onConfirm={doCancel}
      />
    </div>
  );
}
